
<!-- src/views/TillsView.vue -->
<template>
  <div class="section coredeck-page-section">
    <div class="row valign-wrapper">
      <div class="col s12 m6">
        <h4>Tills</h4>
      </div>
      <div class="col s12 m6 right-align">
        <button
          class="btn waves-effect waves-light"
          @click="addTill"
          :disabled="isSubmitting || venueModeLoading"
        >
          Add Till
        </button>
        <button
          class="btn waves-effect waves-light"
          style="margin-left: 8px"
          @click="submitTills"
          :disabled="!canSubmit || isSubmitting || venueModeLoading"
          :title="!canSubmit ? 'Missing venue / week ending / trading date' : 'Submit tills'"
        >
          {{ isSubmitting ? 'Submitting…' : 'Submit Tills' }}
        </button>
      </div>
    </div>

    <div
      v-if="venueModeLoading"
      class="card-panel blue-grey lighten-5 blue-grey-text text-darken-4"
    >
      Loading venue till settings...
    </div>

    <div
      v-else-if="venueModeError"
      class="card-panel amber lighten-4 brown-text text-darken-4"
    >
      {{ venueModeError }}
      <div style="margin-top: 6px">
        Tills are using the standard cash venue mode.
      </div>
    </div>

    <div
      v-else-if="isCashless"
      class="card-panel teal lighten-5 teal-text text-darken-4"
    >
      <div class="cashless-heading">
        <i class="material-icons">credit_card</i>
        <strong>Cashless venue</strong>
      </div>
      <div style="margin-top: 8px">
        Cash denominations and float are not required.
        The counted total is PDQ + Fatsoma + Collins.
      </div>
    </div>

    <!-- TILL SUMMARY -->
    <div v-if="!venueModeLoading" class="row">
      <div class="col s12">
        <div class="card">
          <div class="card-content">
            <span class="card-title">Till Summary</span>

            <div class="summary-table-wrap">
              <table class="striped">
                <thead>
                  <tr>
                    <th>Till</th>
                    <th
                      v-if="!isCashless"
                      class="right-align"
                    >
                      Cash Total
                    </th>
                    <th class="right-align">
                      Payments Total
                    </th>
                    <th class="right-align">
                      X-Read
                    </th>
                    <th class="right-align">
                      Variance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="t in tills"
                    :key="'summary-' + t.id"
                  >
                    <td>{{ t.id }}</td>

                    <td
                      v-if="!isCashless"
                      class="right-align"
                    >
                      {{ gbp(computeCashTotal(t)) }}
                    </td>

                    <td class="right-align">
                      {{ gbp(computePaymentTotal(t)) }}
                    </td>

                    <td class="right-align">
                      {{ gbp(computeXReadTotal(t)) }}
                    </td>

                    <td class="right-align">
                      <strong
                        :class="{
                          'red-text': computeVariance(t) < 0
                        }"
                      >
                        {{ gbp(computeVariance(t)) }}
                      </strong>
                    </td>
                  </tr>

                  <tr v-if="!tills.length">
                    <td
                      :colspan="isCashless ? 4 : 5"
                      class="grey-text"
                    >
                      No tills created
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MESSAGES -->
    <div v-if="infoMsg" class="row">
      <div class="col s12">
        <div class="card blue lighten-5">
          <div class="card-content blue-text text-darken-3">
            {{ infoMsg }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="errorMsg" class="row">
      <div class="col s12">
        <div class="card red lighten-5">
          <div class="card-content red-text text-darken-2">
            <strong>Error:</strong> {{ errorMsg }}
          </div>
        </div>
      </div>
    </div>

    <!-- TILL CARDS -->
    <div v-if="!venueModeLoading" class="row">
      <div
        v-for="till in tills"
        :key="till.id"
        class="col s12 m6 l4"
      >
        <div class="card till-card">
          <div class="card-content">

            <div class="till-card-heading">
              <span class="card-title">
                Till {{ till.id }}
              </span>
              <span
                v-if="isCashless"
                class="cashless-till-label"
              >
                Cashless
              </span>
            </div>

            <!-- Keep both columns visible on mobile -->
            <div class="till-table-wrap">
              <table class="striped till-entry-table">
                <colgroup>
                  <col class="till-label-column" />
                  <col class="till-value-column" />
                </colgroup>

                <thead>
                  <tr>
                    <th>
                      {{ isCashless ? 'Payments' : 'Cash' }}
                    </th>
                    <th class="right-align">
                      Amount
                    </th>
                  </tr>
                </thead>

                <tbody>

                  <!-- CASH INPUTS -->
                  <template v-if="!isCashless">
                    <tr
                      v-for="row in cashOnlyRows"
                      :key="row.key"
                    >
                      <td>{{ row.label }}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          class="till-money-input"
                          :value="displayMoneyValue(till[row.key])"
                          @input="onMoneyInput(till, row.key, $event.target.value)"
                          :disabled="isSubmitting"
                        />
                      </td>
                    </tr>

                    <tr class="total-row">
                      <td>
                        <strong>Cash Total</strong>
                      </td>
                      <td class="right-align">
                        <strong>
                          {{ gbp(computeCashTotal(till)) }}
                        </strong>
                      </td>
                    </tr>

                    <tr class="section-break">
                      <td colspan="2"></td>
                    </tr>

                    <tr>
                      <td>Float</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          class="till-money-input"
                          :value="displayMoneyValue(till.lessFloat)"
                          @input="onMoneyInput(till, 'lessFloat', $event.target.value)"
                          :disabled="isSubmitting"
                        />
                      </td>
                    </tr>

                    <tr class="section-break">
                      <td colspan="2"></td>
                    </tr>
                  </template>

                  <!-- PAYMENTS: CASH AND CASHLESS -->
                  <tr class="section-header-row">
                    <td colspan="2">
                      <strong>Payments</strong>
                    </td>
                  </tr>

                  <tr
                    v-for="row in paymentRows"
                    :key="row.key"
                  >
                    <td>{{ row.label }}</td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="till-money-input"
                        :value="displayMoneyValue(till[row.key])"
                        @input="onMoneyInput(till, row.key, $event.target.value)"
                        :disabled="isSubmitting"
                      />
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong>Total Payments</strong>
                    </td>
                    <td class="right-align">
                      <strong>
                        {{ gbp(computePaymentTotal(till)) }}
                      </strong>
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong>SubTotal</strong>
                    </td>
                    <td class="right-align">
                      <strong>
                        {{ gbp(computeSubTotal(till)) }}
                      </strong>
                    </td>
                  </tr>

                  <tr class="section-break">
                    <td colspan="2"></td>
                  </tr>

                  <!-- X-READ -->
                  <tr class="section-header-row">
                    <td colspan="2">
                      <strong>X-Read</strong>
                    </td>
                  </tr>

                  <tr
                    v-for="row in xReadRows"
                    :key="row.key"
                  >
                    <td>{{ row.label }}</td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="till-money-input"
                        :value="displayMoneyValue(till[row.key])"
                        @input="onMoneyInput(till, row.key, $event.target.value)"
                        :disabled="isSubmitting"
                      />
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong>Totals</strong>
                    </td>
                    <td class="right-align">
                      <strong>
                        {{ gbp(computeXReadTotal(till)) }}
                      </strong>
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td>
                      <strong>Variance</strong>
                    </td>
                    <td class="right-align">
                      <strong
                        :class="{
                          'red-text': computeVariance(till) < 0
                        }"
                      >
                        {{ gbp(computeVariance(till)) }}
                      </strong>
                    </td>
                  </tr>

                  <!-- COMMENTS -->
                  <tr class="comments-row">
                    <td colspan="2">
                      <label
                        class="comments-label"
                        :for="'till-notes-' + till.id"
                      >
                        Issues / Comments
                      </label>

                      <textarea
                        :id="'till-notes-' + till.id"
                        v-model="till.notes"
                        class="materialize-textarea till-comments"
                        placeholder="Add any issues, corrections, or comments for this till..."
                        :disabled="isSubmitting"
                      ></textarea>
                    </td>
                  </tr>

                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { doc, getDoc } from "firebase/firestore";
import { auth, authReady, db } from "@/firebase";
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";
import { upsertTillsForTradingDay } from "@/scripts/sqlApiHelper/tillsApi";

const route = useRoute();
const context = useVenueContext();

const tills = ref([]);
const isSubmitting = ref(false);
const errorMsg = ref("");
const infoMsg = ref("");
const venueModeLoading = ref(false);
const venueModeError = ref("");
const isCashless = ref(false);

const cashOnlyRows = [
  { key: "note50", label: "£50" },
  { key: "note20", label: "£20" },
  { key: "note10", label: "£10" },
  { key: "note5", label: "£5" },
  { key: "coin2", label: "£2" },
  { key: "coin1", label: "£1" },
  { key: "p50", label: "50p" },
  { key: "p20", label: "20p" },
  { key: "p10", label: "10p" },
  { key: "p5", label: "5p" },
  { key: "copper", label: "Copper" },
];

const paymentRows = [
  { key: "pdqInput", label: "PDQ" },
  { key: "fatsomaInput", label: "Fatsoma" },
  { key: "collinsInput", label: "Collins" },
];

const xReadRows = [
  { key: "xReadLiquor", label: "Liquor" },
  { key: "xReadFood", label: "Food" },
  { key: "xReadDoor", label: "Door" },
  { key: "xReadTickets", label: "Tickets" },
  { key: "xReadOther", label: "Other" },
];

function unrefish(value) {
  return value && typeof value === "object" && "value" in value
    ? value.value
    : value;
}

function toLocalISODate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    return "";
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isoFromUnknown(raw) {
  if (!raw) return "";

  if (typeof raw === "string") {
    const str = raw.trim();
    if (!str) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

    const d = new Date(str);
    return Number.isNaN(d.getTime())
      ? str.replace(/\//g, "-")
      : toLocalISODate(d);
  }

  if (
    typeof raw === "object" &&
    typeof raw.toDate === "function"
  ) {
    return toLocalISODate(raw.toDate());
  }

  if (raw instanceof Date) {
    return toLocalISODate(raw);
  }

  return "";
}

function getLocalStorageValue(keys) {
  if (typeof window === "undefined") return "";

  for (const key of keys) {
    const val = localStorage.getItem(key);
    if (val) return val;
  }
  return "";
}

function getRouteValue(keys) {
  for (const key of keys) {
    const val = route.params?.[key] || route.query?.[key];
    if (val) return val;
  }
  return "";
}

function getSundayWeekEndingFromISO(dateISO) {
  if (!dateISO) return "";

  const d = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";

  d.setDate(d.getDate() + (7 - d.getDay()) % 7);
  return toLocalISODate(d);
}

const venueId = computed(() =>
  unrefish(context.venueId) ||
  unrefish(context.venue_id) ||
  unrefish(context.activeVenueId) ||
  getLocalStorageValue([
    "venueId",
    "venue_id",
    "activeVenueId",
    "selectedVenueId",
    "currentVenueId",
    "venue",
  ]) ||
  getRouteValue([
    "venueId",
    "venue_id",
    "venue",
  ]) ||
  ""
);

const tradingDateISO = computed(() => {
  const raw =
    unrefish(context.tradingDate) ||
    unrefish(context.trading_date) ||
    unrefish(context.businessDate) ||
    unrefish(context.business_date) ||
    unrefish(context.selectedDate) ||
    getLocalStorageValue([
      "tradingDate",
      "tradingDateISO",
      "trading_date",
      "businessDate",
      "business_date",
      "selectedDate",
      "currentTradingDate",
    ]) ||
    getRouteValue([
      "tradingDate",
      "trading_date",
      "businessDate",
      "date",
    ]);

  return isoFromUnknown(raw) || toLocalISODate(new Date());
});

const weekEndingISO = computed(() => {
  const raw =
    unrefish(context.weekEndingDate) ||
    unrefish(context.weekEnding) ||
    unrefish(context.weekEndingISO) ||
    unrefish(context.week_ending_date) ||
    getLocalStorageValue([
      "weekEndingDate",
      "weekEnding",
      "weekEndingISO",
      "week_ending_date",
      "currentWeekEnding",
    ]) ||
    getRouteValue([
      "weekEnding",
      "weekEndingISO",
      "weekEndingDate",
      "week_ending_date",
      "weekId",
    ]);

  return (
    isoFromUnknown(raw) ||
    getSundayWeekEndingFromISO(tradingDateISO.value)
  );
});

const canSubmit = computed(() =>
  Boolean(
    venueId.value &&
    weekEndingISO.value &&
    tradingDateISO.value &&
    tills.value.length &&
    !venueModeLoading.value
  )
);

// NUMBER AND CURRENCY HELPERS
function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function clamp2(value) {
  return Math.round(toNumber(value) * 100) / 100;
}

function displayMoneyValue(value) {
  const n = toNumber(value);
  return n === 0 ? "" : n;
}

function onMoneyInput(till, key, raw) {
  if (raw === "" || raw == null) {
    till[key] = 0;
    return;
  }

  const n = Number(raw);
  if (!Number.isFinite(n)) return;

  till[key] = clamp2(Math.max(0, n));
}

function gbp(value) {
  return clamp2(value).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// CASH HELPERS
function clearCashFields(till) {
  if (!till) return;

  for (const row of cashOnlyRows) {
    till[row.key] = 0;
  }

  till.lessFloat = 0;
}

function clearAllCashFields() {
  for (const till of tills.value) {
    clearCashFields(till);
  }
}

// CALCULATIONS
function computeCashTotal(till) {
  if (isCashless.value) return 0;

  return cashOnlyRows.reduce(
    (sum, row) => sum + toNumber(till[row.key]),
    0
  );
}

function computePdqTotal(till) {
  return toNumber(till.pdqInput);
}

function computePaymentTotal(till) {
  return paymentRows.reduce(
    (sum, row) => sum + toNumber(till[row.key]),
    0
  );
}

function computeSubTotal(till) {
  if (isCashless.value) {
    return computePaymentTotal(till);
  }

  return (
    computeCashTotal(till) +
    computePaymentTotal(till) -
    toNumber(till.lessFloat)
  );
}

function computeXReadTotal(till) {
  return xReadRows.reduce(
    (sum, row) => sum + toNumber(till[row.key]),
    0
  );
}

function computeVariance(till) {
  return computeSubTotal(till) - computeXReadTotal(till);
}

// TILL STATE
function buildEmptyTill(id) {
  const till = {
    id,
    lessFloat: 0,
    notes: "",
  };

  for (const row of [
    ...cashOnlyRows,
    ...paymentRows,
    ...xReadRows,
  ]) {
    till[row.key] = 0;
  }

  return till;
}

function addTill() {
  const nextId = tills.value.length
    ? Math.max(
        ...tills.value.map((x) => Number(x.id) || 0)
      ) + 1
    : 1;

  tills.value.push(buildEmptyTill(nextId));
}

function ensureTwoTills() {
  if (tills.value.length > 2) {
    tills.value.splice(2);
  }

  while (tills.value.length < 2) {
    addTill();
  }
}

function resetAllTills() {
  for (const till of tills.value) {
    for (const row of [
      ...cashOnlyRows,
      ...paymentRows,
      ...xReadRows,
    ]) {
      till[row.key] = 0;
    }

    till.lessFloat = 0;
    till.notes = "";
  }
}

// FIRESTORE BREAKDOWN
function buildLinesForTill(till) {
  const lines = [];

  const addLine = (line_type, label, amount) => {
    lines.push({
      line_type,
      label,
      amount: clamp2(amount),
    });
  };

  if (!isCashless.value) {
    for (const row of cashOnlyRows) {
      addLine("cash", row.label, till[row.key]);
    }

    addLine(
      "cash_total",
      "Cash Total",
      computeCashTotal(till)
    );

    addLine("float", "Float", till.lessFloat);
  }

  // Retain existing PDQ lines for compatibility
  addLine("pdq", "Input", till.pdqInput);

  addLine(
    "pdq_total",
    "Total PDQ",
    computePdqTotal(till)
  );

  // New payment channels
  addLine(
    "payment",
    "Fatsoma",
    till.fatsomaInput
  );

  addLine(
    "payment",
    "Collins",
    till.collinsInput
  );

  addLine(
    "payments_total",
    "Total Payments",
    computePaymentTotal(till)
  );

  addLine(
    "subtotal",
    "SubTotal",
    computeSubTotal(till)
  );

  for (const row of xReadRows) {
    addLine(
      "xread",
      row.label,
      till[row.key]
    );
  }

  addLine(
    "declared",
    "X-Read Total",
    computeXReadTotal(till)
  );

  addLine(
    "variance",
    "Variance",
    computeVariance(till)
  );

  return lines;
}

// VENUE CASH / CASHLESS MODE
async function loadVenueMode() {
  venueModeError.value = "";
  isCashless.value = false;

  const id = String(venueId.value || "").trim();
  if (!id) return;

  venueModeLoading.value = true;

  try {
    const snap = await getDoc(
      doc(db, "venues", id)
    );

    if (!snap.exists()) {
      throw new Error(
        `Venue "${id}" could not be found.`
      );
    }

    isCashless.value =
      snap.data()?.is_cashless === true;

    if (isCashless.value) {
      clearAllCashFields();
    }
  } catch (e) {
    console.error(
      "[TillsView] Failed to load venue cashless setting:",
      e
    );

    isCashless.value = false;
    venueModeError.value =
      e?.message ||
      "Could not read the venue cashless setting.";
  } finally {
    venueModeLoading.value = false;
  }
}

// SAVE CONTEXT
watch(
  [venueId, weekEndingISO, tradingDateISO],
  ([v, we, td]) => {
    if (typeof window === "undefined") return;

    if (v) {
      localStorage.setItem("venueId", v);
    }

    if (we) {
      localStorage.setItem("weekEnding", we);
    }

    if (td) {
      localStorage.setItem("tradingDate", td);
    }
  },
  { immediate: true }
);

// WATCH VENUE
watch(
  venueId,
  async (newVenueId, oldVenueId) => {
    if (!newVenueId) {
      isCashless.value = false;
      venueModeError.value = "";
      return;
    }

    if (
      newVenueId !== oldVenueId ||
      oldVenueId === undefined
    ) {
      await loadVenueMode();
    }
  },
  { immediate: true }
);

onMounted(() => {
  ensureTwoTills();
});

// SUBMIT TILLS
async function submitTills() {
  try {
    errorMsg.value = "";
    infoMsg.value = "";

    await authReady;

    if (!auth.currentUser) {
      throw new Error("Not logged in");
    }

    if (venueModeLoading.value) {
      throw new Error(
        "Venue settings are still loading."
      );
    }

    if (!canSubmit.value) {
      throw new Error(
        `Missing required data. Venue: ${venueId.value || "missing"}, week ending: ${weekEndingISO.value || "missing"}, trading date: ${tradingDateISO.value || "missing"}.`
      );
    }

    isSubmitting.value = true;

    if (isCashless.value) {
      clearAllCashFields();
    }

    const payload = {
      venue_id: venueId.value,
      week_ending_date: weekEndingISO.value,
      trading_date: tradingDateISO.value,

      tills: tills.value.map((till, index) => {
        const declared_total = clamp2(
          computeXReadTotal(till)
        );

        const counted_total = clamp2(
          computeSubTotal(till)
        );

        const notes = String(
          till.notes || ""
        ).trim();

        return {
          till_number:
            Number(till.id) || index + 1,

          declared_total,
          counted_total,

          variance: clamp2(
            counted_total - declared_total
          ),

          notes: notes || null,

          lines: buildLinesForTill(till),
        };
      }),
    };

    await upsertTillsForTradingDay(payload);

    resetAllTills();

    infoMsg.value =
      "Tills submitted successfully.";

  } catch (e) {
    console.error(
      "[TillsView] Submit failed:",
      e
    );

    errorMsg.value =
      e?.message || String(e);

  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.till-card {
  width: 100%;
  border-radius: 12px;
}

.till-card .card-content {
  width: 100%;
  box-sizing: border-box;
}

.till-card-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.till-card-heading .card-title {
  margin: 0;
}

.cashless-heading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cashless-heading .material-icons {
  font-size: 22px;
}

.cashless-till-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 14px;
  font-size: 11px;
  line-height: 1;
  font-weight: 700;
  text-transform: uppercase;
  background: #e0f2f1;
  color: #00695c;
}

.till-table-wrap {
  width: 100%;
  overflow: visible;
}

.till-entry-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.till-label-column {
  width: 52%;
}

.till-value-column {
  width: 48%;
}

.till-entry-table th,
.till-entry-table td {
  box-sizing: border-box;
  vertical-align: middle;
}

.till-entry-table th:first-child,
.till-entry-table td:first-child {
  padding-left: 8px;
  padding-right: 10px;
}

.till-entry-table th:last-child,
.till-entry-table td:last-child {
  padding-left: 10px;
  padding-right: 8px;
}

.till-money-input {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  text-align: right;
}

.till-entry-table input[type="number"] {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

.section-header-row td,
.total-row td {
  font-weight: 700;
}

.section-break td {
  height: 10px;
  padding: 0 !important;
  background: transparent !important;
}

.comments-row td {
  padding-top: 20px !important;
}

.comments-label {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  font-weight: 700;
  color: #424242;
}

.till-comments {
  display: block;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  min-height: 85px;
  padding: 10px !important;
  box-sizing: border-box !important;
  border: 1px solid #ddd !important;
  border-radius: 8px;
  background: #fff;
  resize: vertical;
}

.till-comments:focus {
  border: 1px solid #aaa !important;
  box-shadow: none !important;
}

.summary-table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media only screen and (max-width: 600px) {
  .row.valign-wrapper {
    display: block;
  }

  .card .card-content {
    padding: 14px !important;
  }

  .btn,
  .btn-small,
  button.btn,
  button.btn-small {
    width: 100%;
    margin: 4px 0;
  }

  .till-card-heading {
    flex-wrap: wrap;
    gap: 8px;
  }

  .till-label-column {
    width: 48%;
  }

  .till-value-column {
    width: 52%;
  }

  .till-entry-table th,
  .till-entry-table td {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  .till-money-input,
  .till-entry-table input[type="number"] {
    width: 100% !important;
    min-width: 0 !important;
  }

  .till-comments {
    width: 100% !important;
    min-width: 0 !important;
  }
}
</style>
